import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Download, AlertTriangle, Clock, CheckCircle, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: "bg-blue-50 text-blue-700",
  reviewed: "bg-yellow-50 text-yellow-700",
  approved: "bg-green-50 text-green-700",
  rejected: "bg-red-50 text-red-700",
};
const statusIcons = { new: AlertTriangle, reviewed: Clock, approved: CheckCircle, rejected: XCircle };

export function ApplicationsList({ contacts, setContacts, authApi, canEdit }) {
  const updateStatus = async (id, status) => {
    await authApi().patch(`/contact/${id}`, { status });
    setContacts(contacts.map((c) => c.id === id ? { ...c, status } : c));
    toast.success(`Status updated to ${status}.`);
  };

  const deleteContact = async (id) => {
    await authApi().delete(`/contact/${id}`);
    setContacts(contacts.filter((c) => c.id !== id));
  };

  if (contacts.length === 0) {
    return <div className="bg-white border border-[#0B2545]/10 p-8 text-center text-sm text-slate-500">No applications yet.</div>;
  }

  return (
    <div className="space-y-3">
      {contacts.map((c) => {
        const StatusIcon = statusIcons[c.status] || AlertTriangle;
        return (
          <div key={c.id} data-testid={`application-${c.id}`} className="bg-white border border-[#0B2545]/10 p-5 hover:border-[#13A8BD]/30 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-sm font-bold text-[#0B2545]">{c.organization_name}</h3>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 ${statusColors[c.status] || 'bg-slate-100 text-slate-500'}`}>
                    <StatusIcon size={10} /> {c.status}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{c.contact_person} — {c.email} {c.phone && `— ${c.phone}`}</p>
                {c.standard_requested && <p className="text-[10px] text-[#13A8BD] font-bold uppercase mt-1">{c.standard_requested}</p>}
                <p className="text-xs text-slate-600 mt-2 bg-[#F7F9FC] p-3 border border-[#0B2545]/5">{c.message}</p>
              </div>
              <div className="flex flex-col items-end gap-2">
                {canEdit && (
                  <Select value={c.status} onValueChange={(val) => updateStatus(c.id, val)}>
                    <SelectTrigger className="w-32 h-8 text-[10px] rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["new", "reviewed", "approved", "rejected"].map((s) => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                <a href={`${API}/contact/${c.id}/pdf`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#13A8BD] font-bold uppercase flex items-center gap-1 hover:text-[#0B2545]">
                  <Download size={10} /> PDF
                </a>
                {canEdit && <button onClick={() => deleteContact(c.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={14} /></button>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function SubscribersList({ newsletters, setNewsletters, authApi, canEdit }) {
  const deleteNewsletter = async (id) => {
    await authApi().delete(`/newsletter/${id}`);
    setNewsletters(newsletters.filter((n) => n.id !== id));
  };

  if (newsletters.length === 0) {
    return <div className="bg-white border border-[#0B2545]/10 p-8 text-center text-sm text-slate-500">No subscribers.</div>;
  }

  return (
    <div className="bg-white border border-[#0B2545]/10">
      {newsletters.map((n) => (
        <div key={n.id} className="p-4 border-b border-[#0B2545]/10 last:border-b-0 flex items-center justify-between">
          <span className="text-sm text-[#0B2545]">{n.email}</span>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-400">{new Date(n.created_at).toLocaleDateString()}</span>
            {canEdit && <button onClick={() => deleteNewsletter(n.id)} className="text-slate-300 hover:text-red-500"><Trash2 size={12} /></button>}
          </div>
        </div>
      ))}
    </div>
  );
}
